import axios, { AxiosResponse } from 'axios';
import { DOCUMENTS_URL, SERVER } from '../constants/environment.constants';
import { BaseResponse } from '../common/io/base.response';
import { BaseModel } from '../common/io/orm/base';
import { ILoginResponse } from '../common/io/login.response';
import { ICurrentUserResponse } from '../common/io/current-user.response';
import { IUsers, USERS_API } from '../common/io/orm/users';
import { ICompany } from '../common/io/orm/company';
import { COMPANY_ID, JWT } from '../constants/auth.constants';
import { getFromLocalStorage, setToLocalStorage } from './storage.util';
import Pagination from '../common/io/pagination';
import { ELEMENTS_PER_PAGE } from '../constants/constants';
import { getLoggedCompanyId } from '../common/function/utilities';

const ALL: string = '/find/all';
const TOP: string = '/find/top';
const ALL_FAVOURITE: string = ALL + '-favourite';
const ALL_VIEW: string = '/all-view';
const ALL_DELTA: string = ALL + '/delta';
const ALL_SUGGESTION: string = ALL + '/suggestion';
const AUTH: string = 'auth';
const AUTH_CURRENT: string = AUTH + '/current';
const AUTH_LOGIN: string = AUTH + '/login';
const AUTH_RESET_PASSWORD: string = AUTH + '/reset-password';
export const AUTH_REFRESH_TOKEN: string = AUTH + '/refresh/token';
export const ALERTS_COUNT: string = 'alerts/count-';
const AUTH_REGISTER: string = AUTH + '/register';
const AUTH_REGISTER_COMPANY: string = AUTH + '/register-company';
const COUNT: string = '/count';
const COUNT_DELTA: string = COUNT + '/delta';
const DELETE: string = '/delete-';
const INSERT: string = '/create';
const INSERT_ALL: string = INSERT + '/all';
const LAST: string = '/last';
const PARENT: string = '/parent';
const UPLOAD: string = '/upload-';
const COMPLETE: string = '/complete-create';

export async function axiosChangeLanguage(data: any): Promise<any | null> {
	const response: AxiosResponse = await axios.put(SERVER + 'auth/change-language/' + data.id, data);
	if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
		return response?.data;
	}
	throw new Error(response?.status?.toString());
}

export async function axiosChangeAliasLanguage(data: any): Promise<any | null> {
	const response: AxiosResponse = await axios.put(SERVER + 'auth/alias/change-language/' + data.id, data);
	if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
		return response?.data;
	}
	throw new Error(response?.status?.toString());
}

export async function axiosChangePassword(data: any): Promise<any | null> {
	const response: AxiosResponse = await axios.put(SERVER + 'auth/change-password/' + data.id, data);
	if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
		return response?.data;
	}
	throw new Error(response?.status?.toString());
}

export async function axiosChangeAliasPassword(data: any): Promise<any | null> {
    const response: AxiosResponse = await axios.put(SERVER + 'auth/alias/change-password/' + data.id, data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCompanyMap<ICompanyMap>(api: string, id: number): Promise<ICompanyMap | null> {
    const response: AxiosResponse<ICompanyMap> = await axios.get(SERVER + api + '/map/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCountDelta<T extends BaseModel>(api: string, filter: T): Promise<BaseResponse<any> | null> {
    const response: AxiosResponse<BaseResponse<any>> = await axios.post(SERVER + api + COUNT_DELTA, { body: filter });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCount<T extends BaseModel>(api: string, filter: any): Promise<BaseResponse<any> | null> {
    const response: AxiosResponse<BaseResponse<any>> = await axios.post(SERVER + api + COUNT, { body: filter });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCreate<T extends BaseModel>(api: string, data: T): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.post(SERVER + api + INSERT, data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCreateList<T extends BaseModel>(api: string, data: T[]): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.post(SERVER + api + '/list', data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCreateAll<T extends BaseModel>(api: string, data: T[]): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<any>> = await axios.post(SERVER + api + INSERT_ALL, data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosDelete(api: string, id: number): Promise<void> {
    const response: AxiosResponse = await axios.delete(SERVER + api + '/delete/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosTerminate(api: string, id: number): Promise<void> {
    const response: AxiosResponse = await axios.delete(SERVER + api + '/terminate/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosReactivate(api: string, id: number): Promise<void> {
    const response: AxiosResponse = await axios.put(SERVER + api + '/reactivate/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosDeleteFile(api: string, type: string, id: number): Promise<void> {
    const response: AxiosResponse = await axios.delete(SERVER + api + DELETE + type + '/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosDetail<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/detail/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosEmployees<T>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + '/employees', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFilter<T extends BaseModel>(api: string, filter: any, pagination?: any): Promise<BaseResponse<T> | null> {
	const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + '/filter', { body: filter, pagination: pagination });
	if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
		return response?.data;
	}
	throw new Error(response?.status?.toString());
}

export async function axiosFindAll<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindType(api: string): Promise<BaseResponse<String> | null> {
	const response: AxiosResponse<BaseResponse<String>> = await axios.post(SERVER + api + '/find/type');
	if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
		return response?.data;
	}
	throw new Error(response?.status?.toString());
}

export async function axiosFindAllEmployee<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/employee', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllFuture<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/future', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosCompareCandidateAI<T extends BaseModel>(api: string, data: { ids: number[] }): Promise<void> {
	const response: AxiosResponse<BaseResponse<any>> = await axios.post(SERVER + api, data);
	if (HTTP_STATUS_OK.indexOf(response?.status) < 0) {
		throw new Error(response?.status?.toString());
	}
}

export async function axiosFindAllCandidate<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/candidate', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindTop<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + TOP, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllFavourite<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL_FAVOURITE, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllByParent<T extends BaseModel>(api: string, idParent: number, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/by-parent/' + idParent, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosAlerts(): Promise<any> {
    const response: any = await axios.get(SERVER + ALERTS_COUNT + (getFromLocalStorage(COMPANY_ID) ? 'company/' + getFromLocalStorage(COMPANY_ID) : 'user'));
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllActive<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/active', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllActiveWithStatistics<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/active/statistics', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllArchived<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/archived', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllNotActive<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/not-active', { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllByRole<T extends BaseModel>(api: string, role: string, filter: any, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/' + role, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllWithParticipant<T extends BaseModel>(api: string, filter: any, participant: number, pagination?: any, orderBy?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL + '/' + participant, { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllAndView<T extends BaseModel>(api: string, filter: any, pagination?: any, orderBy?: any, suffix?: string): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL_VIEW + (suffix ?? ''), { body: filter, pagination: pagination, orderBy: orderBy });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllDelta<T extends BaseModel>(api: string, filter: any, pagination?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL_DELTA, { body: filter, pagination: pagination });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllSuggestion<T extends BaseModel>(api: string, filter: any): Promise<BaseResponse<T> | null> {
	if (getLoggedCompanyId()) {
		return axiosFindAllSuggestionCompany(api, filter);
	}
	return axiosFindAllSuggestionUser(api, filter);
}

export async function axiosFindAllSuggestionCompany<T extends BaseModel>(api: string, filter: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL_SUGGESTION + '/company', { body: filter });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindAllSuggestionUser<T extends BaseModel>(api: string, filter: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + ALL_SUGGESTION + '/user', { body: filter });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindLast<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/' + id + LAST);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindParent<T extends BaseModel>(api: string, filter: any, pagination?: any): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.post(SERVER + api + PARENT, { body: filter, pagination: pagination });
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindById<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/find/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindActive<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/active/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindByUsername<T extends BaseModel>(api: string, username: string): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/username/' + username);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsById<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdBase<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/base');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdWith<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/with');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdSkills<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/skills');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdSkillsHistory<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/skills-history');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdSoftSkills<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/soft-skills');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosStatisticsByIdFor<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/statistics/' + id + '/for');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindEmployeesBySkill<T extends BaseModel>(api: string, idTeam: number, idSkill: number): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<T>> = await axios.get(SERVER + api + '/' + idTeam + '/skill/' + idSkill);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindByIdAndView<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/' + id + '/view');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindByUuid<T extends BaseModel>(api: string, uuid: string): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/uuid/' + uuid);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindOther<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/find/other/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosFindByUserId<T extends BaseModel>(api: string, id: number): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.get(SERVER + api + '/by-user/' + id);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export function axiosGenerateInvoice(id: number): void {
	axios
		.get(`${SERVER}download/invoice/${id}`, { responseType: 'blob' })
		.then(response => download(response, `invoice-${id}.pdf`));
}

export function axiosGenerateEHCv(id: number): void {
	axios
		.get(`${SERVER}download/cv/${id}`, { responseType: 'blob' })
		.then(response => download(response, `curriculum-vitae-${id}.pdf`));
}

export function axiosDownloadCalendar(id: number): void {
	axios
		.get(`${SERVER}download/calendar/${id}`, { responseType: 'blob' })
		.then(response => download(response, `calendar-${id}.ics`));
}

export function axiosDownloadEmployees(idCompany: number): void {
	axios
		.get(`${SERVER}download/employees/${idCompany}`, { responseType: 'blob' })
		.then(response => download(response, `employees-${idCompany}.csv`));
}

export function axiosDownloadStatisticsCompanyQueries(idCompany: number, status: string): void {
	axios
		.get(`${SERVER}download/statistics/company/${idCompany}/queries/${status}`, { responseType: 'blob' })
		.then(response => download(response, `company-queries-statistics-${idCompany}-${status}.csv`));
}

export function axiosDownloadStatisticsCandidateQueries(idCandidate: number): void {
	axios
		.get(`${SERVER}download/statistics/candidate/${idCandidate}/queries`, { responseType: 'blob' })
		.then(response => download(response, `candidate-queries-statistics-${idCandidate}.csv`));
}

export function axiosDownloadStudents(idCompany: number): void {
	axios
		.get(`${SERVER}download/students/${idCompany}`, { responseType: 'blob' })
		.then(response => download(response, `students-${idCompany}.csv`));
}

function download(response: AxiosResponse, filename: string): void {
	const blob: any = response.data;
	const contentDisposition: any = response.headers['content-disposition'];
	const fileNameMatch: any = contentDisposition?.match(/filename="?([^"]+)"?/);
	const fileName: any = fileNameMatch?.[1] ?? filename;

	const url: string = window.URL.createObjectURL(blob);

	const a: HTMLAnchorElement = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

export async function axiosInit<T extends BaseModel>(api: string, data: T): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.post(SERVER + api + '/init', data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosLoggedUser(): Promise<IUsers | null> {
    const response: AxiosResponse<ICurrentUserResponse, any> = await axios.get(SERVER + AUTH_CURRENT);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data?.current;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosLogin(username: string, password: string): Promise<ILoginResponse | null> {
    const response: AxiosResponse<ILoginResponse, any> = await axios.post(SERVER + AUTH_LOGIN, { username, password });
    if (response?.data?.accessToken) {
        setToLocalStorage(JWT, response?.data?.accessToken);
    }
    return response?.data;
}

export async function axiosResetPassword(username: string): Promise<void> {
    await axios.post(SERVER + AUTH_RESET_PASSWORD, { username });
}

export async function axiosRefreshToken(): Promise<ILoginResponse | null> {
    const response: AxiosResponse<ILoginResponse, any> = await axios.get(SERVER + AUTH_REFRESH_TOKEN);
    if (response?.data?.accessToken) {
        setToLocalStorage(JWT, response?.data?.accessToken);
    }
    return response?.data;
}

export async function axiosExistsByUsername(username: string): Promise<boolean> {
	try {
		const response: AxiosResponse = await axios.get(`${SERVER}${USERS_API}/username/${username}/exists/`);
		return response.data.exists;
	} catch (error) {
		return false;
	}
};

export async function axiosRegister(user: IUsers): Promise<ILoginResponse | null> {
	const response: AxiosResponse<ILoginResponse, any> = await axios.post(SERVER + AUTH_REGISTER, user);
	if (response?.data?.accessToken) {
		setToLocalStorage(JWT, response?.data?.accessToken);
	}
	return response?.data;
}

export async function axiosRegisterAccess(user: IUsers): Promise<IUsers | null> {
	const response: AxiosResponse<IUsers, any> = await axios.post(SERVER + AUTH_REGISTER + '-access', user);
	return response?.data;
}

export async function axiosRegisterCompany(company: ICompany): Promise<void> {
    await axios.post(SERVER + AUTH_REGISTER_COMPANY, company);
}

export async function axiosUpdate<T extends BaseModel>(api: string, data: T): Promise<T | null> {
    const response: AxiosResponse<T> = await axios.put(SERVER + api + '/update/' + data.id, data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosView(api: string, id: number | string): Promise<void> {
    await axios.post(SERVER + api + '/view/' + id);
}

export async function axiosUploadFile<T extends BaseModel>(api: string, file: File, type: string, id: number, name?: string): Promise<T | null> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse<T> = await axios.post(SERVER + api + UPLOAD + type + '/' + id + (name ? '/' + name : ''), formData);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export function axiosDownloadFile(filePath: string, fileName?: string): void {
    const url: string = DOCUMENTS_URL + filePath;
    const link: HTMLAnchorElement = document.createElement("a");
    link.href = url;
    link.download = fileName ?? filePath;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export async function axiosWidget<ICompany>(api: string, id: number): Promise<ICompany | null> {
    const response: AxiosResponse<ICompany, any> = await axios.put(SERVER + api + '/' + id + '/widget');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosHide<INotification>(api: string, id: number): Promise<INotification | null> {
    const response: AxiosResponse<INotification, any> = await axios.put(SERVER + api + '/' + id + '/hide');
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

export async function axiosComplete<T extends BaseModel>(api: string, data: T): Promise<BaseResponse<T> | null> {
    const response: AxiosResponse<BaseResponse<any>> = await axios.post(SERVER + api + COMPLETE, data);
    if (HTTP_STATUS_OK.indexOf(response?.status) >= 0) {
        return response?.data;
    }
    throw new Error(response?.status?.toString());
}

const HTTP_STATUS_OK: number[] = [
    200, 201, 202, 203
];

export function filterName(filter?: string): any {
	return { name: filter };
}

export function filterValue(filter?: string): any {
	return { value: filter };
}

export function getPagination(page?: number, elementsPerPage?: number): Pagination {
	return {
		page: page ?? 0,
		elementsPerPage: elementsPerPage ?? ELEMENTS_PER_PAGE
	};
}
